/* eslint-disable prettier/prettier */
// importing libraties
import * as React from "react";
import {
  Modal,
  Grid,
  TextField,
  Link,
  InputLabel,
} from "@mui/material";
import { getYodaDateToday } from "../utils/DateHelper";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

// importing hooks
import { useAddPatient } from "../hooks/patients/useAddPatient";
import { useFlash } from "../FlashContext";

// app setting imports
import { AuthenticationContext } from "../Context";
import GeneralModal from "../modals/GeneralModal";
import { MAX_EMAIL_LENGTH, getEmailError, validateEmail } from "../utils/DemographicValidationHelper";
import { MAX_NAME_LENGTH, getNameError } from "../utils/DemographicValidationHelper";

interface Props {
  logo_src: string;
  button_src: string;
  inviteOpen: boolean;
  setInviteOpen: any;
}

const normalizeForSave = (value: string): string => {
 return value
    ?.trim()
    .replace(/\s+/g, " ")
    ?? "";
};

const AddPatientInvite: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const {
    email,
    setEmail,
    firstName,
    setFirstName,
    middleName,
    setMiddleName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
    addPatient,
    error,
    setError,
    disabledButton,
  } = useAddPatient(authenticationSetting.csrfToken, { showToast });

  const [showValidationErrors, setShowValidationErrors] = React.useState<boolean>(false);

  const handleModalClose = () => {
    setShowValidationErrors(false);
    props.setInviteOpen(false);
  };

  const handleAddPatient = () => {
    setShowValidationErrors(true);
    addPatient();
  };

  const handleDateChange = (date: Date | null) => {
  if (!date) {
    // If user cleared the picker, store null
    setDateOfBirth(null);
    return;
  }

  // Format to YYYY-MM-DD with zero padding for month/day
  const stringDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  setDateOfBirth(stringDate);
};

  return (
    <GeneralModal
      open={props.inviteOpen}
      title={"Invite Patient to Starfield"}
      successCallback={handleAddPatient}
      closeCallback={handleModalClose}
      containerClassName="add-patient-modal"
      width="500px"
      isLoading={disabledButton}
    >
      <Grid container className="form-container" spacing={1}>
        <Grid container>
          <Grid item xs={12}>
            <InputLabel htmlFor="gender" className="field-label2">
              First Name*
            </InputLabel>
          </Grid>
          <Grid item xs={12} className="field-container">
            <TextField
              id="firstName"
              size="small"
              required
              value={firstName}
              variant="outlined"
              className="the-field"
              inputProps={{ maxLength: MAX_NAME_LENGTH }}
              helperText={getNameError(firstName, "First name", showValidationErrors)}
              error={!!getNameError(firstName, "First name", showValidationErrors)}
              onChange={(event) => setFirstName((event.target.value))}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <InputLabel htmlFor="gender" className="field-label2">
              Middle Name
            </InputLabel>
          </Grid>
          <Grid item xs={12} className="field-container">
            <TextField
              id="middle_name"
              size="small"
              value={middleName}
              className="the-field"
              variant="outlined"
              inputProps={{ maxLength: MAX_NAME_LENGTH }}
              helperText={getNameError(middleName, "Middle name", showValidationErrors)}
              error={!!getNameError(middleName, "Middle name", showValidationErrors)}
              onChange={(event) => {setMiddleName((event.target.value));}}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <InputLabel htmlFor="gender" className="field-label2">
              Last Name*
            </InputLabel>
          </Grid>
          <Grid item xs={12} className="field-container">
            <TextField
              id="last_name"
              size="small"
              value={lastName}
              required
              className="the-field"
              variant="outlined"
              inputProps={{ maxLength: MAX_NAME_LENGTH }}
              helperText={getNameError(lastName, "Last name", showValidationErrors)}
              error={!!getNameError(lastName, "Last name", showValidationErrors)}
              onChange={(event) => {
                setLastName((event.target.value));}}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <InputLabel htmlFor="date_of_birth" className="field-label2">
              Date of Birth*
            </InputLabel>
          </Grid>
          <Grid item xs={12} className="date-field-container">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                disableToolbar
                autoOk={true}
                variant="inline"
                format="MM/dd/yyyy"
                maxDate={new Date(getYodaDateToday())}
                margin="normal"
                id="date-picker-inline"
                value={dateOfBirth ? new Date(dateOfBirth) : null}                 
                onChange={handleDateChange}
                className="date-field patient-dob-field"
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </MuiPickersUtilsProvider>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <InputLabel htmlFor="gender" className="field-label2">
              Email Address*
            </InputLabel>
          </Grid>
          <Grid item xs={12} className="field-container">
            <TextField
              id="email-address"
              size="small"
              variant="outlined"
              className="the-field"
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              helperText={getEmailError(email)}
              error={!!getEmailError(email)}
              required
              value={email}
              type="email"
              onChange={(event) => setEmail(event.target.value)}
            />
          </Grid>
        </Grid>
      </Grid>
    </GeneralModal>
  );
};

export default AddPatientInvite;
